import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import ModalBody from '@components/Modals/components/ModalBody';
import ModalBreadcrumb from '@components/Modals/components/ModalBreadcrumb';
import ModalHeader from '@components/Modals/components/ModalHeader';
import loadable from '@loadable/component';
import getGamePalette from '@helpers/getGamePalette';
import getGameName from '@helpers/getGameName';
import useRemoveAddGameParams from '@hooks/useRemoveAddGameParams';
import { useState } from 'react';
import { useAddGameContext } from '../context/AddGameProvider';
import useAddGameBreadcrumbManager from '@hooks/useAddGameBreadcrumbManager';
import useSetDefaultGameService from '../hooks/useSetDefaultGameService';
import getAnalyticsFlowStep from '@domain/Analytics/helpers/getAnalyticsFlowStep';
import ContentCarousel from '@components/UI/ContentCarousel';
import AddGameOAuthConnectContent from '../AddGameOAuthConnectContent';
import AddGameConnectStatus from '../AddGameConnectStatus';
import AddGameInterstitial from '../AddGameInterstitial';
import useHandleAddGameEvents from '../hooks/useHandleAddGameEvents';
import PersonaPlatformSelection from './PersonaPlatformSelection';
import { useUserState } from '../../../context/UserContext';
import useUserGamesListener from '../../Hooks/useUserGamesListener';
const GameIcon = loadable(() => import('@components/Icons/GameIcon'));
const AddEaGame = ({ eaGameKey, onStepChange, onConnectConfirm, shouldShowSelectBreadcrumb = true, onBackToSelect, successButtonLabel = 'Continue', failButtonLabel = 'Retry', shouldGoToConnectionStatus = false, onClose, }) => {
    const gameStepBreadcrumbs = [
        {
            id: 'oauth-connect',
            component: 'Connect',
            analyticsFlowStep: 'connect game',
        },
        ...(eaGameKey === 'battlefield-6'
            ? [
                {
                    id: 'persona-platform-selection',
                    component: 'Platform',
                    analyticsFlowStep: 'select persona platform',
                },
            ]
            : []),
    ];
    const removeAddGameParams = useRemoveAddGameParams();
    const breadcrumbStepOffset = shouldShowSelectBreadcrumb ? 1 : 0;
    const addGameContext = useAddGameContext();
    const { requireAgePrompt, isProviderReady, gameConnectionStatus, fetched } = addGameContext;
    const connectionStatusPage = gameConnectionStatus === 'success' ? 1 : -1;
    const [currentStepIndex, setCurrentStepIndex] = useState(shouldGoToConnectionStatus ? connectionStatusPage : 0);
    const { userProfile } = useUserState();
    const { triggerReload: reloadUserGames } = useUserGamesListener(userProfile === null || userProfile === void 0 ? void 0 : userProfile.handle);
    const { breadcrumbIndex, breadcrumbs, breadcrumbIsVisible, setBreadcrumbIsVisible, displayBreadcrumbsUpTo } = useAddGameBreadcrumbManager({
        gameStepBreadcrumbs,
        startingIndex: breadcrumbStepOffset,
        includeSelectBreadcrumb: shouldShowSelectBreadcrumb,
        requireAgePrompt,
    });
    useHandleAddGameEvents({
        state: addGameContext,
        onAddGameError: () => setCurrentStepIndex(-1),
        onAddGameSuccess: () => setCurrentStepIndex(eaGameKey === 'battlefield-6' ? 1 : -1), // go to Persona Selection screen after successful connection
    });
    const handleBreadcrumbClick = (idx, breadcrumb) => {
        if (breadcrumb.id === 'select') {
            // Invoke onBackToSelect to dismiss current view
            if (onBackToSelect) {
                onBackToSelect(eaGameKey);
            }
        }
        else if (idx >= breadcrumbStepOffset) {
            setCurrentStepIndex(idx - breadcrumbStepOffset);
        }
    };
    const handleStepChange = (changedStepIndex, stepName) => {
        if (stepName === 'game-connect-status') {
            setBreadcrumbIsVisible(false);
        }
        else {
            setBreadcrumbIsVisible(true);
            displayBreadcrumbsUpTo(stepName);
        }
        if (onStepChange) {
            const analyticsFlowStep = getAnalyticsFlowStep(gameConnectionStatus, changedStepIndex, gameStepBreadcrumbs);
            onStepChange(changedStepIndex, stepName, analyticsFlowStep, gameStepBreadcrumbs.length);
        }
    };
    const handleConnectSuccessClick = () => {
        reloadUserGames();
        if (onConnectConfirm) {
            onConnectConfirm();
        }
    };
    const handleConnectRetryClick = () => {
        removeAddGameParams();
        setCurrentStepIndex(0); // Reset to the first step
    };
    const handleClose = () => {
        const isSuccessPage = breadcrumbIndex === -1;
        if (onClose) {
            onClose(gameConnectionStatus === 'success', isSuccessPage);
        }
    };
    const handleContinue = () => {
        setCurrentStepIndex(currentStepIndex + 1);
    };
    useSetDefaultGameService();
    return (_jsxs(_Fragment, { children: [_jsx(ModalHeader, { title: _jsx("span", { "data-ignore": true, children: getGameName(eaGameKey) }), icon: _jsx(GameIcon, { game: eaGameKey, showGameColor: true }), subtitle: "Connect your account", paletteName: getGamePalette(eaGameKey), onClose: handleClose }), _jsx(ModalBreadcrumb, { currentIndex: breadcrumbIndex, breadcrumbs: breadcrumbs, onClick: handleBreadcrumbClick, isVisible: breadcrumbIsVisible }), _jsxs(ModalBody, { children: [isProviderReady && (_jsxs(ContentCarousel, { name: "battlefield-6", currentStep: currentStepIndex, onStepChange: handleStepChange, children: [_jsx(AddGameOAuthConnectContent, { stepName: "oauth-connect" }), eaGameKey === 'battlefield-6' && (_jsx(PersonaPlatformSelection, { stepName: "persona-platform-selection", onContinue: handleContinue })), _jsx(AddGameConnectStatus, { onConfirm: handleConnectSuccessClick, onRetry: handleConnectRetryClick, status: gameConnectionStatus !== null && gameConnectionStatus !== void 0 ? gameConnectionStatus : 'error', gameKey: eaGameKey, successButtonLabel: successButtonLabel, failButtonLabel: failButtonLabel, errorMessage: (fetched === null || fetched === void 0 ? void 0 : fetched.isError) ? fetched.errorMessage : undefined, stepName: "game-connect-status" })] })), !isProviderReady && _jsx(AddGameInterstitial, {})] })] }));
};
export default AddEaGame;
